<template>
    <div v-if="showApplication && initialChecksComplete" class="h-full mx-auto w-full max-w-1920" style="margin-bottom: 0;">
        <div v-if="fetchingApplication" class="flex-grow flex items-center justify-center">
            <loading></loading>
        </div>

        <error-403 v-else-if="!can.view"></error-403>
        <error-404 v-else-if="!application"></error-404>

        <div v-else class="h-full flex flex-col">
            <header class="flex-none flex items-center mb-16">
                <div class="flex items-center gap-12">
                    <div class="space-y-8">
                        <router-link :disabled="!prevApplication"
                                     :to="{name: 'application', params: {id: prevApplication}}"
                                     class="block">
                            <tooltip :content="$t('general.previous')"
                                     :disabled="!prevApplication"
                                     position="right"
                            >
                                <simple-button
                                    look="secondary"
                                    icon="keyboard-arrow-up"
                                    small
                                    :disabled="!prevApplication"
                                />
                            </tooltip>
                        </router-link>
                        <router-link :disabled="!nextApplication"
                                     :to="{name: 'application', params: {id: nextApplication}}"
                                     class="block">
                            <tooltip :content="$t('general.next')"
                                     :disabled="!nextApplication"
                                     position="right"
                            >
                                <simple-button
                                    look="secondary"
                                    icon="keyboard-arrow-down"
                                    small
                                    :disabled="!nextApplication"
                                />
                            </tooltip>
                        </router-link>
                    </div>
                    <avatar
                        :color="application.stageColor"
                        :image="application.image_url"
                        size="large"
                        shadow="shadow"
                        shape="round"
                    >
                        {{ application.initials }}
                    </avatar>
                    <div>
                        <div class="flex items-center mb-4">
                            <button
                                :class="application.favorite ? 'hover:text-semantic-warning-light text-semantic-warning' : 'hover:text-semantic-warning text-primary-20'"
                                :disabled="working"
                                role="button"
                                @click="setFavorite"
                            >
                                <svg-icon class="mr-12 w-32 h-32" :type="application.favorite ? 'star-filled' : 'star'"></svg-icon>
                            </button>
                            <h1 class="font-semibold text-heading-2 leading-none">{{ application.fullName }}</h1>
                        </div>
                        <span
                            class="text-body-sm text-primary-80 leading-none hover:text-secondary cursor-pointer transition duration-150"
                            @click="goToJob"
                            v-if="can.viewJob"
                        >
                            {{ this.translated(application.stage.job.title, this.locale, true) }}
                            {{ location }}
                        </span>
                        <span
                            class="text-body-sm text-primary-80 leading-none"
                            v-else
                        >
                            {{ this.translated(application.stage.job.title, this.locale, true) }}
                            {{ location }}
                        </span>
                        <template v-if="application.referrer || application.referrer_source">
                            <span v-if="application.stage"> |</span>
                            <span class="text-body-sm text-primary-80">
                            {{ $t('applications.source') }}:
                            <a :class="application.referrer ? 'hover:text-secondary' : ''"
                               :href="application.referrer || undefined"
                               class="text-primary-80 no-underline leading-none transition duration-150" target="_blank">
                                {{ application.referrer_source }}
                            </a>
                        </span>
                        </template>
                    </div>
                </div>

                <!-- interims-->
                <div v-if="application.referrer_source === 'interim' && application.interim"
                     class="flex items-center ml-40 pl-40 border-l-2 border-primary-20">
                    <img :src="application.interim.logo" alt="interim logo" class="max-w-40"/>

                    <div class="ml-14 text-primary">
                        <svg-icon class="h-16 w-16" type="location-city"></svg-icon>
                        <span class="block first-letter:uppercase mt-4">{{ application.interim.name }}</span>
                    </div>
                </div>

                <div class="flex flex-grow items-end justify-end gap-8">
                    <move-stage :application="application" :can="can.manageJob"></move-stage>

                    <simple-button
                        v-if="can.manage"
                        look="icon"
                        icon="mail"
                        display="neutral"
                        @click="startSendingMail"
                    />

                    <simple-button
                        v-if="can.manage && application.stage"
                        look="icon"
                        icon="calendar-add-on"
                        display="neutral"
                        @click="startSendingInvitation"
                    />

                    <simple-button
                        v-if="can.manage"
                        look="icon"
                        icon="edit"
                        display="neutral"
                        @click="edit"
                    />

                    <simple-button
                        v-if="can.manage"
                        look="icon"
                        icon="download"
                        display="neutral"
                        @click="startExport"
                    />

                    <simple-dropdown v-if="can.manage && showOfficientBtn" position="right">
                        <template #trigger>
                            <simple-button
                                look="icon"
                                icon="extension"
                                display="neutral"
                            />
                        </template>
                        <template #content>
                            <ul class="flex flex-wrap">
                                <li class="p-8 flex flex-col">
                                    <a class="bg-white hover:shadow-sm flex-grow flex items-center border border-primary-20 rounded-6 font-semibold text-center leading-snug hover:border-primary-40 trans-ease p-16 cursor-pointer"
                                       @click="showAddToOfficientBtn ? startAddingToOfficient() : goToOfficient()"
                                    >
                                        <div class="flex flex-col items-center justify-center gap-4">
                                            <business-avatar
                                                icon="officient"
                                                class="bg-officient text-white"
                                            />
                                            <span v-if="showAddToOfficientBtn">
                                                {{ $t('applications.add_to_officient') }}
                                            </span>
                                            <span v-if="showAddedToOfficientBtn" class="text-secondary">
                                                {{ $t('applications.go_to_officient') }}
                                            </span>
                                        </div>
                                    </a>
                                </li>
                            </ul>
                        </template>
                    </simple-dropdown>

                    <simple-dropdown position="right">
                        <template #trigger>
                            <simple-button
                                look="icon"
                                icon="more-horiz"
                                display="neutral"
                            />
                        </template>
                        <template #content="{ close }">
                            <nav
                                aria-orientation="vertical"
                                class="bg-white relative rounded shadow"
                                role="menu"
                            >
                                <dropdown-item
                                    icon="extension"
                                    :input-label="$t('applications.assessments.request_button')"
                                    v-if="$refs.assessments.showFeature"
                                    @click="startRequestingAssessment(); close()"
                                />
                                <dropdown-item
                                    icon="swap-vert"
                                    :input-label="$t('applications.move')"
                                    v-if="can.manageJob"
                                    @click="startMovingApplication(); close()"
                                />
                                <dropdown-item
                                    icon="visibility"
                                    :input-label="$t('applications.anonymise')"
                                    @click="confirmAnonymise(); close()"
                                />
                                <dropdown-item
                                    danger
                                    icon="delete"
                                    :input-label="$t('applications.delete')"
                                    @click="confirmDelete(); close()"
                                />
                            </nav>
                        </template>
                    </simple-dropdown>
                </div>
            </header>

            <section class="grow max-h-full overflow-hidden bg-white shadow-md rounded flex">
                <div class="w-1/2 border-r border-primary-20 flex-none p-48 overflow-y-scroll">

                    <div class="flex items-center mb-8">
                        <ApplicationButton
                            :condition="can.manage"
                            :title="application.tags.length ? $t('applications.manage_tags') : $t('applications.add_tags')"
                            icon="add-circle"
                            size="small"
                            @click="toggleTagsManager"
                        />

                        <ul class="ml-8 flex-grow leading-none inline-flex flex-wrap gap-8">
                            <tag
                                v-for="tag in tags"
                                :key="tag.id"
                                display="info"
                                no-icon
                            >
                                {{ tag.title }}
                            </tag>
                        </ul>
                    </div>

                    <tags-manager v-if="managingTags"
                                  :title="title"
                                  :parent="application"
                                  :service="service()"
                                  :model="model"
                                  type="applications"
                                  :can-manage="can.manage"
                                  @close="toggleTagsManager(false)"
                    />

                    <div v-if="socialMediaData && socialMediaData.length" class="flex items-center justify-start mb-16">
                        <span class="first-letter:uppercase mr-6">{{ $t('settings.company.social') }}</span>

                        <a v-for="(socialMedia, i) in socialMediaData" :href="socialMedia.value" target="_blank">
                            <svg-icon
                                :class="[i !== socialMediaData.length - 1 ? 'mr-8' : '', {'text-primary' : socialMedia.name ==='linkedin' || socialMedia.name ==='facebook'}]"
                                :type="socialMedia.icon"
                                class="h-24 w-24 hover:text-secondary cursor-pointer"/>
                        </a>
                    </div>

                    <div class="space-y-32">
                        <application-personal-info
                            :application="application"
                            :can-manage="can.manage"
                            @edit="edit"
                        />

                        <application-motivation
                            :application="application"
                            :can-view="can.view"
                        />

                        <application-seek-questions
                            v-if="seekFeatureEnabled"
                            :application="application"
                        />

                        <application-questions
                            v-if="application.stage"
                            :application_questions="application.dynamic_questions"
                            :form_questions="application.stage.job.dynamic_form.questions"
                            :questions="application.dynamic_questions"
                        />

                        <application-questions
                            v-if="application.stage"
                            :application_questions="application.dynamic_questions_interim"
                            :form_questions="application.stage.job.dynamic_form_interim.questions"
                            :questions="application.dynamic_questions_interim"
                            context="interim"
                        />

                        <application-scores
                            :application="application"
                            :can-manage="can.manage && !!application.stage"
                            class="mb-32"
                        />

                        <application-interviews
                            :application="application"
                            :can-manage="can.manage && !!application.stage"
                            v-if="can.view"
                            @createInterview="startSendingInvitation"
                        />

                        <application-documents
                            :application="application"
                            :can-manage="can.manage"
                        />

                        <application-experience
                            :application="application"
                            :can-manage="can.manage"
                        />
                    </div>

                </div>

                <div class="w-1/2 flex flex-col">
                    <div v-if="application.expires_in && application.expires_in < 31"
                         class="w-full flex items-center justify-center space-x-8 p-16 border-b-2 border-primary-20">
                        <svg-icon :class="application.expires_in < 0 ? 'text-semantic-error' : 'text-semantic-warning'"
                                  class="max-w-16 max-h-16" type="warning"></svg-icon>
                        <span class="text-primary-80">{{
                                application.expires_in < 0 ? $t('applications.expired_since') : $t('applications.expires_in')
                            }}</span>
                        <div :class="application.expires_in < 0 ? 'bg-semantic-error' : 'bg-semantic-warning'"
                             class="font-semibold text-body-sm px-8 py-4 rounded-6 text-white"
                        >{{ $t('applications.days', {days: Math.abs(application.expires_in)}) }}
                        </div>
                    </div>

                    <div class="p-16 border-b border-primary-20">
                        <div class="flex items-center">
                            <div class="pl-8 mr-auto">
                                <simple-button
                                    :display="isSubscribed ? 'primary' : 'neutral'"
                                    :icon="isSubscribed ? 'notifications-filled' : 'notifications'"
                                    icon-position="left"
                                    look="text"
                                    @click="toggleSubscription"
                                >
                                    {{ isSubscribed ? $t('applications.turn_off_notifications') : $t('applications.turn_on_notifications') }}
                                </simple-button>
                            </div>
                            <permission-users :target="application" class="pl-24 mr-16"/>
                            <simple-button
                                v-if="can.manageStrict"
                                look="icon"
                                icon="add"
                                display="neutral"
                                small
                                @click="manageSubscriptions"
                            />
                        </div>
                    </div>

                    <div class="overflow-y-auto flex-1 flex flex-col">
                        <div class="mt-16">
                            <tabs
                                :active-key="activeTab"
                                class="w-full"
                                :items="timelineTabs"
                                @set-active="activeTab = $event"
                            />
                        </div>

                        <keep-alive>
                            <timeline v-if="activeTab === 'timeline'" ref="timeline" :application="application"></timeline>
                        </keep-alive>

                        <keep-alive>
                            <comment-box
                                v-if="activeTab === 'timeline' && can.view"
                                ref="commentBox"
                                :application="application"
                            />
                        </keep-alive>

                        <keep-alive>
                            <tasks-detail
                                v-if="activeTab === 'tasks'"
                                :key="'tasks_' + _uid"
                                :context="application"
                                class="p-32"
                            ></tasks-detail>
                        </keep-alive>

                        <keep-alive>
                            <application-related
                                v-if="activeTab === 'related'"
                                :application="application"
                            ></application-related>
                        </keep-alive>
                    </div>
                </div>
            </section>
        </div>

        <application-editor v-if="can.manage && editing"
                            :application="application"
                            :stage="application.stage"
                            @close="stopEdit"
        />

        <application-exporter
            v-if="exporting"
            :applications="[application.id]"
            :request-filters="{}"
            :stages="[]"
            @close="stopExport"
        />

        <mail-creator
            v-if="can.manage && sendingMail"
            context="single"
            :applications="[application]"
            :request-filters="{}"
            :total-applications="1"
            @close="stopSendingMail"
        />

        <invitation-creator
            v-if="can.manage && sendingInvitation"
            context="single"
            :applications="[application]"
            :total-applications="1"
            :can-edit-recipients="false"
            @close="stopSendingInvitation"
        />

        <popup-modal-confirm
            v-if="can.manage && deleting"
            :confirmText="$t('general.delete_subject', {subject: $t('applications.this_application')})"
            :message="$t('general.confirm_delete_subject', {subject: $t('applications.application_from', {subject: application.fullName})}) + ' ' + $t('general.confirm_remove_permanently')"
            show-exclamation
            :title="$t('applications.delete_application')"
            :working="performingDeleteRequest"
            @close="cancelDelete"
            @confirm="deleteApplication"
        />

        <popup-modal-confirm
            v-if="can.manage && anonymising"
            :confirmText="$t('applications.anonymise')"
            :message="$t('applications.anonymise_confirm', {subject: application.fullName}) + ' ' + $t('applications.anonymise_confirm_message')"
            show-exclamation
            :title="$t('applications.anonymise_title')"
            :working="performingAnonymisationRequest"
            @close="cancelAnonymise"
            @confirm="anonymiseApplication"
        />

        <permission-manager
            v-if="managingSubscriptions"
            :resource-title="application.fullName"
            :target="application"
            @close="stopManagingSubscribers"
        />

        <move-to-job
            v-if="movingApplication"
            :application="application"
            @close="stopMovingApplication"
        />

        <add-to-officient
            v-if="addingToOfficient"
            :application="application"
            @close="stopAddingToOfficient"
        />

        <feature ref="assessments" feature-key="assessments" sub-key="wrapper">
            <assessment
                v-if="requestingAssessment"
                :application="application"
                @close="stopRequestingAssessment"
            />
        </feature>
    </div>
</template>

<script>
import {mapStores, mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from "../state/store";
import {useAuthenticationStore} from "../state/modules/authentication";
import {useApplicationsStore} from "../state/modules/applications";
import {useCompaniesStore} from "../state/modules/companies";
import {useNotificationsStore} from "../state/modules/notifications";
import {ADD_NOTIFICATION} from '../state/mutation-types';

import Application from '../models/Application';
import Contact from '../models/Contact';
import Document from "../models/Document";
import Invitation from '../models/Invitation';
import Subscription from '../models/Subscription';

import * as subscriptionService from '../services/SubscriptionService';
import * as applicationService from '../services/ApplicationService';
import {normalizeJson} from '../../shared/helpers';
import styles from '../../../../../styles.config.js';

import CommentBox from '../components/ui/CommentBox.vue';
import Timeline from '../components/Timeline.vue';
import Avatar from '../components/ui/Avatar.vue';
import RoundButton from '../components/ui/RoundButton.vue';
import ApplicationEditor from '../components/ui/ApplicationEditor.vue';
import ApplicationExporter from '../components/ui/ApplicationExporter.vue';
import ApplicationInterviews from '../components/ApplicationInterviews.vue';
import ApplicationDocuments from '../components/ApplicationDocuments.vue';
import MoveStage from '../components/ui/MoveStage.vue';
import SvgIcon from '../components/ui/SvgIcon.vue';
import Tooltip from '../components/navigation/Tooltip.vue';
import SimpleButton from '../components/ui/SimpleButton.vue';
import MailCreator from '../components/ui/MailCreator.vue';
import ApplicationExperience from '../components/ApplicationExperience.vue';
import ApplicationRelated from '../components/ApplicationRelated.vue';
import ApplicationScores from '../components/ApplicationScores.vue';
import SimpleDropdown from '../components/ui/SimpleDropdown.vue';
import PopupModalConfirm from '../components/ui/PopupModalConfirm.vue';
import Error403 from '../components/ui/Error403.vue';
import Error404 from '../components/ui/Error404.vue';
import Loading from '../components/ui/Loading.vue';
import MoveToJob from '../components/ui/MoveToJob.vue';
import AddToOfficient from '../components/ui/AddToOfficient.vue';
import TasksDetail from '../components/ui/TasksDetail.vue';
import PermissionUsers from '../components/ui/PermissionUsers.vue';
import PermissionManager from '../components/ui/PermissionManager.vue';
import ApplicationQuestions from '../components/ApplicationQuestions.vue';
import ApplicationSeekQuestions from '../components/ApplicationSeekQuestions.vue';
import Assessment from '../components/ui/Assessment.vue';
import InvitationCreator from '../components/ui/InvitationCreator.vue';
import Feature from '../components/Feature.vue';
import TagsManager from "../components/TagsManager.vue";
import ApplicationMotivation from "../components/ApplicationMotivation.vue";
import ApplicationPersonalInfo from "../components/ApplicationPersonalInfo.vue";
import ApplicationButton from "../components/ui/ApplicationButton.vue";
import Tag from "../components/ui/Tag.vue";
import BusinessAvatar from "../components/ui/BusinessAvatar.vue";
import DropdownItem from "../components/ui/DropdownItem.vue";
import Tabs from "../components/navigation/Tabs.vue";

export default {
    name: 'ApplicationDetail',
    components: {
        Tabs,
        DropdownItem,
        BusinessAvatar,
        Tag,
        ApplicationButton,
        ApplicationMotivation,
        ApplicationPersonalInfo,
        Feature,
        InvitationCreator,
        ApplicationQuestions,
        PermissionManager,
        PermissionUsers,
        TagsManager,
        'loading': Loading,
        'comment-box': CommentBox,
        'timeline': Timeline,
        'avatar': Avatar,
        'round-button': RoundButton,
        'application-editor': ApplicationEditor,
        'application-exporter': ApplicationExporter,
        'application-interviews': ApplicationInterviews,
        'application-documents': ApplicationDocuments,
        'move-stage': MoveStage,
        'svg-icon': SvgIcon,
        'tooltip': Tooltip,
        'simple-button': SimpleButton,
        'mail-creator': MailCreator,
        'application-experience': ApplicationExperience,
        'application-related': ApplicationRelated,
        'application-seek-questions': ApplicationSeekQuestions,
        'application-scores': ApplicationScores,
        'simple-dropdown': SimpleDropdown,
        'popup-modal-confirm': PopupModalConfirm,
        'error-403': Error403,
        'error-404': Error404,
        'move-to-job': MoveToJob,
        'add-to-officient': AddToOfficient,
        'tasks-detail': TasksDetail,
        'assessment': Assessment,
    },
    data: function () {
        return {
            stages: {
                'date-desc': this.$t('general.sort.date.desc'),
                'date-asc': this.$t('general.sort.date.asc'),
                'name-desc': this.$t('general.sort.name.desc'),
                'name-asc': this.$t('general.sort.name.asc'),
            },
            currentStage: 'date-desc',
            commentInputToggled: false,
            editing: false,
            exporting: false,
            sendingMail: false,
            sendingInvitation: false,
            deleting: false,
            anonymising: false,
            performingDeleteRequest: false,
            performingAnonymisationRequest: false,
            fetchingApplication: false,
            managingSubscriptions: false,
            movingApplication: false,
            addingToOfficient: false,
            showApplication: true,
            subscribing: false,
            requestingAssessment: false,
            working: false,
            managingTags: false,
            seekFeatureEnabled: false,
            activeTab: 'timeline',
            initialChecksComplete: false,
        };
    },
    computed: {
        ...mapStores(useApplicationsStore),
        ...mapState(useAuthenticationStore, ['currentUserId', 'currentCompany']),
        ...mapState(useMainStore, ['locale']),
        styles() {
            return styles
        },
        can() {
            return {
                view: this.$can([
                    'applications.view',
                    'applications.view.' + this.$route.params.id,
                    'job-applications.view.' + this.application?.stage?.job?.id,
                ]),
                manage: this.$can([
                    'applications.manage',
                    'applications.manage.' + this.$route.params.id,
                    'job-applications.manage.' + this.application?.stage?.job?.id,
                ]),
                manageStrict: this.$can('applications.manage') || this.application?.stage?.job?.responsible_user_id === this.currentUserId,
                manageJob: this.$can([
                    'jobs.manage',
                    'jobs.manage.' + this.application?.stage?.job?.id,
                ]),
                viewJob: this.$can([
                    'jobs.view',
                    'jobs.view.' + this.application?.stage?.job?.id,
                ]),
            };
        },
        application() {
            return useRepo(Application)
                .with('tags')
                .with('subscriptions', (query) => {
                    query.with('user');
                })
                .with('application_fields')
                .with('stage', (query) => {
                    query.with('job').orderBy('order');
                })
                .with('interim')
                .with('related_applications')
                .with('original_stage', (query) => {
                    query.with('job')
                })
                .with('related_applications', (query) => {
                    query.with('stage', (subQuery) => {
                        subQuery.with('job')
                    })
                })
                .where('company_id', this.currentCompany.id)
                .whereId(parseInt(this.$route.params.id))
                .first();
        },
        timelineTabs() {
            return {
                'timeline': {
                    title: this.$t('applications.timeline'),
                    disabled: false,
                },
                'tasks': {
                    title: this.$t('tasks.tasks'),
                    badge: 'info',
                    count: this.application.tasks_count,
                    disabled: false,
                },
                'related': {
                    title: this.$t('applications.related_applications_general'),
                    badge: 'info',
                    count: Math.max(this.application.related_applications.length, 0),
                    disabled: !this.application.related_applications.length,
                },
            }
        },
        title() {
            return this.application.first_name + ' ' + this.application.last_name;
        },
        model() {
            return useRepo(Application);
        },
        location() {
            return (this.application.stage.job.active_location && this.application.stage.job.active_location.city) ? `- ${this.application.stage.job.active_location.city}` : null;
        },
        showOfficientBtn() {
            // return this.can.manage && this.currentCompany.officient_linked && this.application.stage.onboarding;
            return this.can.manage && this.currentCompany.officient_linked;
        },
        showAddToOfficientBtn() {
            return this.showOfficientBtn && !this.application.added_to_officient;
        },
        showAddedToOfficientBtn() {
            return this.showOfficientBtn && this.application.added_to_officient;
        },
        userSubscription() {
            return this.application.subscriptions.find(subscription => {
                return subscription.user_id === this.currentUserId;
            });
        },
        isSubscribed() {
            return !!this.userSubscription;
        },
        subscriptionToolTip() {
            return this.isSubscribed ? this.$t('applications.turn_off_notifications') : this.$t('applications.turn_on_notifications');
        },
        socialMediaData() {
            return (({
                         facebook,
                         twitter,
                         linkedin,
                         instagram,
                     }) => ([
                {name: 'linkedin', value: linkedin, icon: 'linkedin'},
                {name: 'facebook', value: facebook, icon: 'facebook-large'},
                {name: 'twitter', value: twitter, icon: 'twitter-large'},
                {name: 'instagram', value: instagram, icon: 'instagram'},
            ]))(this.application).filter(entry => !!entry.value);
        },
        nextApplication() {
            const seq = this.applicationsModuleStore.sequence;

            if (!seq || !seq.includes(this.application?.id)) {
                return null;
            }

            return seq[seq.indexOf(this.application.id) + 1];
        },
        prevApplication() {
            const seq = this.applicationsModuleStore.sequence;

            if (!seq || !seq.includes(this.application?.id)) {
                return null;
            }

            return seq[seq.indexOf(this.application.id) - 1];
        },
        tags() {
            if (!this.application.tags || null) {
                return null;
            }

            return this.application.tags = [...new Set(this.application.tags)]
        },
        hasReloaded(){
            return localStorage.getItem('hasReloaded') === 'true';
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        ...mapActions(useMainStore, ['setDocumentTitle']),
        ...mapActions(useCompaniesStore, ['hasFeature']),
        focusCommentInput(e) {
            this.commentInputToggled = true;
        },
        removeCommentInputFocus(e) {
            this.commentInputToggled = false;
        },
        edit() {
            this.editing = true;
        },
        stopEdit() {
            this.editing = false;
        },
        service() {
            return applicationService;
        },
        toggleTagsManager(show = true) {
            this.managingTags = show;
        },
        async toggleSubscription() {
            if (this.subscribing) {
                return;
            }

            this.subscribing = true;
            if (this.isSubscribed) {
                await this.unsubscribeFromApplication();
            } else {
                await this.subscribeToApplication();
            }
            this.subscribing = false;
        },
        startExport() {
            this.exporting = true;
        },
        stopExport() {
            this.exporting = false;
        },
        startAddingToOfficient() {
            this.addingToOfficient = true;
        },
        stopAddingToOfficient() {
            this.addingToOfficient = false;
        },
        goToOfficient() {
            window.open(Jobtoolz.config.officient.app_url + '/people/' + this.application.officient_id);
        },
        subscribeToApplication() {
            return subscriptionService.store({
                data: {
                    attributes: {},
                    relationships: {
                        user: {
                            data: {
                                type: 'user',
                                id: this.currentUserId,
                            },
                        },
                        subable: {
                            data: {
                                type: 'applications',
                                id: this.application.id,
                            },
                        },
                    },
                },
            }).then(result => {
                return useRepo(Subscription).save(normalizeJson(result));
            }).then(inserts => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('applications.notifications_turned_on', {application: this.application.fullName}),
                });
            });
        },
        unsubscribeFromApplication() {
            let subId = this.userSubscription.id;
            return subscriptionService.destroy(subId).then(() => {
                useRepo(Subscription).destroy(subId);
            }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('applications.notifications_turned_off', {application: this.application.fullName}),
                });
            });
        },
        startSendingMail() {
            this.sendingMail = true;
        },
        stopSendingMail() {
            this.sendingMail = false;
        },
        startSendingInvitation() {
            this.sendingInvitation = true;
        },
        stopSendingInvitation() {
            this.sendingInvitation = false;
        },
        confirmDelete() {
            this.deleting = true;
        },
        confirmAnonymise() {
            this.anonymising = true;
        },
        cancelDelete() {
            this.deleting = false;
        },
        cancelAnonymise() {
            this.anonymising = false;
        },
        deleteApplication() {
            this.performingDeleteRequest = true;

            applicationService.destroy(this.application.id).then(() => {

                let applicationId = this.application.id;

                // Currently redirecting to the applications index page.
                // Would be better, after implementing breadcrumbs, if we redirected
                // to the previous page, or if there is no previous page to the applications index.
                this.$router.push({name: 'applications'}).finally(() => {
                    useRepo(Application).destroy(applicationId);
                });
            });
        },
        anonymiseApplication() {
            this.performingAnonymisationRequest = true;

            applicationService.anonymise(this.application.id).then(() => {

                let applicationId = this.application.id;

                // Currently redirecting to the applications index page.
                // Would be better, after implementing breadcrumbs, if we redirected
                // to the previous page, or if there is no previous page to the applications index.
                this.$router.push({name: 'applications'}).finally(() => {
                    useRepo(Application).destroy(applicationId);
                });
            });
        },
        manageSubscriptions() {
            this.managingSubscriptions = true;
        },
        stopManagingSubscribers() {
            this.managingSubscriptions = false;
        },
        startMovingApplication() {
            this.movingApplication = true;
        },
        stopMovingApplication() {
            this.movingApplication = false;
        },
        startRequestingAssessment() {
            this.requestingAssessment = true;
        },
        stopRequestingAssessment() {
            this.requestingAssessment = false;
        },
        fetch() {
            this.fetchingApplication = true;
            return applicationService.find(parseInt(this.$route.params.id)).then(result => {
                return useRepo(Application).save(normalizeJson(result));
            }).finally(() => {
                if (!this.can.view && !this.hasReloaded) {
                    localStorage.setItem('hasReloaded', 'true');
                    window.location.reload();
                    return;
                }

                if (this.hasReloaded) {
                    localStorage.removeItem('hasReloaded');
                }

                this.setDocumentTitle(this.application.fullName);
                this.fetchingApplication = false;
                this.initialChecksComplete = true;
            });
        },
        goToJob() {
            this.$router.push({name: 'job', params: {id: this.application.stage.job_id}});
        },
        setFavorite() {
            this.working = true;
            this.applicationsModuleStore.setFavorite({
                applicationId: this.application.id,
                favorite: this.application.favorite,
            });
            this.working = false;
        },
		setAccessToFeature() {
            const seekFeature = this.hasFeature('seek');
            this.seekFeatureEnabled = !!seekFeature;
        },
    },
    watch: {
        $route(to, from) {
            this.showApplication = false;
            this.fetch();
            this.$nextTick(() => this.showApplication = true);
        },
    },
    mounted() {
        // this checks if the id only contains digits
        if (!/^\d+$/.test(this.$route.params.id)) {
            this.initialChecksComplete = true;
            return this.$router.push({name: 'oops'});
        }

        this.fetchingApplication = true;
        this.fetch()

        Echo.private('applications.' + this.$route.params.id)
            .listen('ContactMailUpdate', ({email}) => {
                if (email.type === 'invitations') {
                    useRepo(Invitation).save(normalizeJson(email));
                } else {
                    useRepo(Contact).save(normalizeJson(email));
                }
            })
            .listen('DocumentUpdate', ({document}) => {
                useRepo(Document).save(normalizeJson(document));
            });

        this.setAccessToFeature();
    },
};
</script>

<style>
.slide-enter-active {
    -moz-transition-duration: 0.3s;
    -webkit-transition-duration: 0.3s;
    -o-transition-duration: 0.3s;
    transition-duration: 0.3s;
    -moz-transition-timing-function: ease-in;
    -webkit-transition-timing-function: ease-in;
    -o-transition-timing-function: ease-in;
    transition-timing-function: ease-in;
}

.slide-leave-active {
    -moz-transition-duration: 0.3s;
    -webkit-transition-duration: 0.3s;
    -o-transition-duration: 0.3s;
    transition-duration: 0.3s;
    -moz-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
    -webkit-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
    -o-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
    transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
}

.slide-enter-to, .slide-leave {
    max-height: 100px;
    overflow: hidden;
}

.slide-enter, .slide-leave-to {
    overflow: hidden;
    max-height: 0;
}
</style>
